"use client"

import { create } from "zustand"

import { getAuthContextState } from "@/features/auth/services/auth-context-store"
import {
  getOfferingTypeService,
  createOfferingTypeService,
  listOfferingTypesService,
  updateOfferingTypeService,
  updateOfferingTypeStatusService,
} from "@/features/church/config/offering-types/services/offering-types.service"
import { sanitizeOfferingTypeInput } from "@/features/church/config/offering-types/store/offering-types.helpers"
import {
  OfferingTypeKind,
  OfferingTypeScope,
  OfferingTypeStatus,
  type CreateOfferingTypeInput,
  type MockProgramOption,
  type OfferingType,
  type OfferingTypesListQuery,
  type OfferingTypesListResponse,
  type UpdateOfferingTypeInput,
} from "@/features/church/config/offering-types/types/offering-type.types"

type OfferingTypesState = {
  offeringTypes: OfferingType[]
  programs: MockProgramOption[]
  total: number
  page: number
  limit: number
  query: Partial<OfferingTypesListQuery>
  isLoading: boolean
  listError: string | null
  actionError: string | null
  seedMockData: () => void
  listOfferingTypes: (query?: Partial<OfferingTypesListQuery>) => Promise<OfferingTypesListResponse>
  getOfferingType: (id: string) => Promise<OfferingType>
  createOfferingType: (input: CreateOfferingTypeInput) => Promise<OfferingType>
  updateOfferingType: (id: string, input: UpdateOfferingTypeInput) => Promise<OfferingType>
  setOfferingTypeStatus: (id: string, status: OfferingTypeStatus) => Promise<OfferingType>
  getById: (id: string) => OfferingType | undefined
}

const DEFAULT_LIMIT = 100

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback
}

function resolveWorkspaceId(workspaceId?: string | null): string {
  const candidate = workspaceId ?? getAuthContextState().activeWorkspaceId
  if (!candidate) {
    throw new Error("errors.workspaceRequired")
  }
  return candidate
}

function toNullableText(value: string | undefined) {
  if (value === undefined) return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function buildCreatePayload(input: CreateOfferingTypeInput, workspaceId: string) {
  const sanitized = sanitizeOfferingTypeInput({
    scope: input.scope,
    ownerBranchId: input.ownerBranchId,
    kind: OfferingTypeKind.PERMANENT,
  })

  return {
    workspaceId,
    name: input.name.trim(),
    description: toNullableText(input.description),
    scope: input.scope,
    ownerBranchId: input.scope === OfferingTypeScope.BRANCH ? sanitized.ownerBranchId ?? null : null,
    status: input.status,
  }
}

function buildUpdatePayload(input: UpdateOfferingTypeInput, existing: OfferingType) {
  const nextScope = input.scope ?? existing.scope
  const nextOwnerBranchId = input.ownerBranchId ?? existing.ownerBranchId ?? undefined

  const sanitized = sanitizeOfferingTypeInput({
    scope: nextScope,
    ownerBranchId: nextOwnerBranchId,
    kind: OfferingTypeKind.PERMANENT,
  })

  return {
    name: input.name?.trim(),
    description: input.description !== undefined ? toNullableText(input.description) : undefined,
    scope: nextScope,
    ownerBranchId: nextScope === OfferingTypeScope.BRANCH ? sanitized.ownerBranchId ?? null : null,
    status: input.status,
  }
}

function toListQuery(query: Partial<OfferingTypesListQuery> | undefined, current: Partial<OfferingTypesListQuery>) {
  const workspaceId = resolveWorkspaceId(query?.workspaceId ?? current.workspaceId)
  const hasQuery = Boolean(query)

  return {
    workspaceId,
    scope: hasQuery && "scope" in (query as object) ? query?.scope : current.scope,
    status: hasQuery && "status" in (query as object) ? query?.status : current.status,
    ownerBranchId:
      hasQuery && "ownerBranchId" in (query as object) ? query?.ownerBranchId : current.ownerBranchId,
    q: hasQuery && "q" in (query as object) ? query?.q : current.q,
    page: hasQuery && "page" in (query as object) ? query?.page ?? 1 : current.page ?? 1,
    limit: hasQuery && "limit" in (query as object) ? query?.limit ?? DEFAULT_LIMIT : current.limit ?? DEFAULT_LIMIT,
  } satisfies OfferingTypesListQuery
}

export const useOfferingTypesStore = create<OfferingTypesState>()((set, get) => ({
  offeringTypes: [],
  programs: [],
  total: 0,
  page: 1,
  limit: DEFAULT_LIMIT,
  query: {},
  isLoading: false,
  listError: null,
  actionError: null,

  seedMockData: () => {
    set({
      offeringTypes: [],
      programs: [],
      total: 0,
      page: 1,
      limit: DEFAULT_LIMIT,
      query: {},
      isLoading: false,
      listError: null,
      actionError: null,
    })
  },

  getById: (id) => get().offeringTypes.find((offeringType) => offeringType.id === id),

  listOfferingTypes: async (query) => {
    const nextQuery = toListQuery(query, get().query)
    set({ isLoading: true, listError: null })

    try {
      const response = await listOfferingTypesService(nextQuery)

      set({
        offeringTypes: response.items,
        total: response.total,
        page: response.page,
        limit: response.limit,
        query: nextQuery,
        isLoading: false,
        listError: null,
      })

      return response
    } catch (error) {
      const message = getErrorMessage(error, "errors.listFailed")
      set({ isLoading: false, listError: message })
      throw error
    }
  },

  getOfferingType: async (id) => {
    set({ isLoading: true, actionError: null })

    try {
      const response = await getOfferingTypeService(id)
      set((state) => ({
        offeringTypes: state.offeringTypes.some((item) => item.id === id)
          ? state.offeringTypes.map((item) => (item.id === id ? response : item))
          : [response, ...state.offeringTypes],
        isLoading: false,
        actionError: null,
      }))

      return response
    } catch (error) {
      const message = getErrorMessage(error, "errors.fetchFailed")
      set({ isLoading: false, actionError: message })
      throw error
    }
  },

  createOfferingType: async (input) => {
    const name = input.name.trim()
    if (name.length < 2) throw new Error("errors.nameMin")

    const workspaceId = resolveWorkspaceId()
    const payload = buildCreatePayload(input, workspaceId)

    set({ isLoading: true, actionError: null })

    try {
      const created = await createOfferingTypeService(payload)
      set((state) => ({
        offeringTypes: [created, ...state.offeringTypes.filter((item) => item.id !== created.id)],
        total: state.total + 1,
        isLoading: false,
        actionError: null,
      }))
      return created
    } catch (error) {
      const message = getErrorMessage(error, "errors.createFailed")
      set({ isLoading: false, actionError: message })
      throw error
    }
  },

  updateOfferingType: async (id, input) => {
    const existing = get().offeringTypes.find((offeringType) => offeringType.id === id)
    if (!existing) throw new Error("errors.notFound")

    if (input.name !== undefined && input.name.trim().length < 2) {
      throw new Error("errors.nameMin")
    }

    const payload = buildUpdatePayload(input, existing)
    set({ isLoading: true, actionError: null })

    try {
      const updated = await updateOfferingTypeService(id, payload)
      set((state) => ({
        offeringTypes: state.offeringTypes.map((offeringType) =>
          offeringType.id === id ? updated : offeringType
        ),
        isLoading: false,
        actionError: null,
      }))
      return updated
    } catch (error) {
      const message = getErrorMessage(error, "errors.updateFailed")
      set({ isLoading: false, actionError: message })
      throw error
    }
  },

  setOfferingTypeStatus: async (id, status) => {
    const existing = get().offeringTypes.find((offeringType) => offeringType.id === id)
    if (!existing) throw new Error("errors.notFound")

    set({ isLoading: true, actionError: null })

    try {
      const updated = await updateOfferingTypeStatusService(id, status)
      set((state) => ({
        offeringTypes: state.offeringTypes.map((offeringType) =>
          offeringType.id === id ? updated : offeringType
        ),
        isLoading: false,
        actionError: null,
      }))
      return updated
    } catch (error) {
      const message = getErrorMessage(error, "errors.statusUpdateFailed")
      set({ isLoading: false, actionError: message })
      throw error
    }
  },
}))
